import React, { useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { INTENT, fmtDateTime } from "../lib/helpers";
import AssistantAvatar from "./AssistantAvatar";

// Conversation d'un projet ou d'une mission (modèle du 12/08, arbitrages de Mélilia du 01/10) — un seul affichage
// pour les trois espaces ; chaque fiche lui passe ses messages déjà mis en forme, et ses propres appels au serveur
// (projet et mission gardent leurs routes et leurs collections, CLAUDE.md §5).
//   messages : [{ id, mine, author, created_at, text, badge?: { kind: "Étape" | "Phase", label }, kindLabel?,
//                 awaiting?, handled?, assistant? }] — assistant : message de mira.ia, précédé de son avatar (02/10)
//   canHandle / onHandle(id) : « À traiter » et « Marquer comme traité » — agence seulement
//   selectLabel, options [{ value, label }] : pastille facultative du message (« Aucune » par défaut)
//   onSend(text, key) : renvoie une promesse ; vide la zone d'écriture si elle réussit
//   sans onSend : lecture seule, closedNote dit pourquoi (mission terminée, recette du 15/10)
//   highlightId : message à montrer en arrivant (« Voir le message » depuis « À traiter »)

// Auteur affiché : l'agence signe « prénom · Metara » (arbitrage n° 6) ; les messages d'avant le 01/10 portent « Metara ».
export const agencyAuthor = (name) => (!name || name === "Metara" ? "Metara" : `${name} · Metara`);

// La pastille situe le message, elle ne dit pas un état : contour marine, jamais une couleur de statut.
const Badge = ({ badge, onDark }) => (
  <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold leading-none px-2 py-1 rounded-full border whitespace-nowrap ${
    onDark ? "border-white/45 text-white bg-white/10" : "border-marine/25 text-marine bg-creme"}`}>
    <Icons.ListChecks size={12} aria-hidden="true" />{badge.kind} · {badge.label}
  </span>
);

export default function Conversation({ messages = [], canHandle = false, onHandle, selectLabel, options = [], placeholder, onSend, highlightId, closedNote, testid = "conversation" }) {
  const [text, setText] = useState("");
  const [key, setKey] = useState("");
  const [sending, setSending] = useState(false);
  const [flash, setFlash] = useState(null);
  const listRef = useRef();
  const shown = useRef(null);

  // En arrivant : le message demandé au centre, souligné un instant (une seule fois) ; sinon, et après chaque nouveau
  // message, le bas de la conversation (les plus récents).
  useEffect(() => {
    const box = listRef.current;
    if (!box) return;
    const target = highlightId && shown.current !== highlightId && box.querySelector(`[data-message-id="${highlightId}"]`);
    if (target) {
      shown.current = highlightId;
      target.scrollIntoView({ block: "center" });
      setFlash(highlightId);
      return;
    }
    box.scrollTop = box.scrollHeight;
  }, [highlightId, messages.length]);
  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(null), 2500);
    return () => clearTimeout(t);
  }, [flash]);

  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      await onSend(t, key || null);
      setText(""); setKey("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible");
    } finally { setSending(false); }
  };

  return (
    <div className="bg-sable rounded-sm border border-marine/10 shadow-sm p-4 sm:p-6" data-testid={testid}>
      <h2 className="font-display text-xl text-marine mb-4">Conversation</h2>
      <div ref={listRef} className="grid grid-cols-1 gap-3.5 mb-4 max-h-[65vh] overflow-y-auto pr-1" data-testid={`${testid}-list`}>
        {messages.length === 0 && <p className="text-sm text-marine/80">Aucun message pour l'instant.</p>}
        {messages.map((m) => {
          const todo = canHandle && m.awaiting && !m.handled;
          return (
            <div key={m.id} data-message-id={m.id} className={`flex flex-col ${m.mine ? "items-end" : "items-start"}`}>
              <div className={`max-w-[88%] sm:max-w-md rounded-sm px-4 py-2.5 transition-shadow ${flash === m.id ? "ring-2 ring-ambre" : ""} ${
                m.mine ? "bg-marine text-white" : "bg-sable border border-marine/30 text-marine"}`}>
                {m.assistant ? (
                  <div className="text-xs mb-1 flex items-center gap-1.5" data-testid={`${testid}-assistant-${m.id}`}>
                    <AssistantAvatar height={18} />
                    <span className="opacity-75">{m.author} · {fmtDateTime(m.created_at)}</span>
                  </div>
                ) : (
                  <div className="text-xs opacity-75 mb-1">{m.author} · {fmtDateTime(m.created_at)}</div>
                )}
                {(m.kindLabel || m.badge) && (
                  <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                    {m.kindLabel && <span className="text-xs font-bold">{m.kindLabel}</span>}
                    {m.badge && m.badge.label && <Badge badge={m.badge} onDark={m.mine} />}
                  </div>
                )}
                <div className="text-sm whitespace-pre-line break-words">{m.text}</div>
              </div>
              {todo && (
                <div className="flex flex-wrap items-center gap-2 mt-1.5" data-testid={`${testid}-todo-${m.id}`}>
                  <span className={`inline-flex items-center text-[11px] font-semibold leading-none px-2 py-1 rounded-sm ${INTENT.attente.cls}`}>À traiter</span>
                  <button type="button" onClick={() => onHandle(m.id)} data-testid={`${testid}-handled-${m.id}`}
                    className="text-xs font-semibold text-marine bg-white border border-marine/30 rounded-sm px-2.5 py-1 hover:bg-marine-50 transition-colors">Marquer comme traité</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {!onSend ? (
        <p className="border-t border-marine/10 pt-4 text-sm text-marine/80" data-testid={`${testid}-closed`}>{closedNote || "La conversation est fermée."}</p>
      ) : (
      <div className="border-t border-marine/10 pt-4 grid grid-cols-1 gap-2">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder={placeholder} data-testid={`${testid}-input`}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
          className="inp resize-y" />
        <div className="flex flex-wrap items-center gap-2 sm:justify-between">
          {options.length > 0 && (
            <label className="flex items-center gap-2 text-sm text-marine/80 basis-full sm:basis-auto min-w-0">{selectLabel}
              <select value={key} onChange={(e) => setKey(e.target.value)} data-testid={`${testid}-key`}
                className="flex-1 sm:flex-none min-w-0 text-sm text-marine bg-white border border-marine/20 rounded-sm px-2 py-1.5">
                <option value="">Aucune</option>
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
          )}
          <button type="button" onClick={send} disabled={!text.trim() || sending} data-testid={`${testid}-send`}
            className="ml-auto inline-flex items-center gap-2 bg-marine text-white rounded-sm px-4 py-2 text-sm font-semibold hover:bg-marine-hover disabled:opacity-50 transition-colors">
            <Icons.Send size={16} aria-hidden="true" /> Envoyer
          </button>
        </div>
      </div>
      )}
    </div>
  );
}
